import { T } from 'gt-react';
import { CameraIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { AttachmentThumbnails } from './AttachmentThumbnails';
import { FeedbackDropzone } from './FeedbackDropzone';
import type { ImageRejection } from './feedbackAttachmentRules';
import type { useFeedbackAttachments } from './useFeedbackAttachments';

/** Screenshots + uploads block of the draft: thumbnails, capture, dropzone. */
export function DraftAttachmentsSection({
  attachments,
  onRejected,
  disabled,
}: {
  attachments: ReturnType<typeof useFeedbackAttachments>;
  onRejected: (rejected: ImageRejection[]) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3">
      <AttachmentThumbnails
        screenshots={attachments.screenshots}
        onRemoveScreenshot={attachments.removeScreenshot}
        files={attachments.files}
        onRemoveFile={attachments.removeFile}
        isCapturing={attachments.isCapturing}
        disabled={disabled}
      />
      {attachments.captureError && (
        <p className="text-xs text-muted-foreground">
          <T context="message when a page screenshot could not be captured">
            Could not capture a screenshot of this page.
          </T>{' '}
          <span className="font-mono">{attachments.captureError}</span>
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={attachments.capturePage}
        disabled={
          disabled || attachments.isCapturing || attachments.remaining <= 0
        }
        className="self-start"
      >
        <CameraIcon className="h-4 w-4" />
        <T context="button that screenshots the current page into the feedback">
          Capture This Page
        </T>
      </Button>
      <FeedbackDropzone
        onFilesSelected={(files) => onRejected(attachments.addFiles(files))}
        disabled={disabled}
        remaining={attachments.remaining}
      />
    </div>
  );
}
